<template>
  <div
    class="sketch-convert-entities-property-section"
    data-testid="sketch-convert-entities-property-section"
  >
    <section class="panel-section">
      <div class="panel-block__header">
        <span class="panel-block__title">已选实体</span>
        <span class="panel-block__meta">{{ selectedItems.length }} 项</span>
      </div>

      <div
        class="selection-box"
        :class="{ 'selection-box--empty': selectedItems.length === 0 }"
        data-testid="convert-entities-selection-box"
      >
        <div v-if="selectedItems.length === 0" class="selection-box__empty">
          请选择要转换的实体
        </div>

        <ul v-else class="selection-box__list">
          <li
            v-for="item in selectedItems"
            :key="item.id"
            class="selection-box__list-item"
          >
            <span class="selection-box__list-label">{{ item.label }}</span>
            <button
              type="button"
              class="selection-box__remove-button"
              title="取消选择"
              aria-label="取消选择"
              data-testid="convert-entities-remove-button"
              @click="handleRemoveSelection(item.id)"
            >
              取消
            </button>
          </li>
        </ul>
      </div>
    </section>

    <section class="panel-section">
      <div class="option-list">
        <n-checkbox
          :checked="constructionChecked"
          data-testid="convert-entities-construction-checkbox"
          @update:checked="handleConstructionToggle"
        >
          作为构造线
        </n-checkbox>
        <n-checkbox
          :checked="selectChainChecked"
          data-testid="convert-entities-select-chain-checkbox"
          @update:checked="handleSelectChainToggle"
        >
          选择链
        </n-checkbox>
        <n-checkbox
          :checked="innerLoopsOneByOneChecked"
          data-testid="convert-entities-inner-loops-one-by-one-checkbox"
          @update:checked="handleInnerLoopsOneByOneToggle"
        >
          逐个内环
        </n-checkbox>
        <n-button
          :disabled="!canSelectAllInnerLoops"
          :loading="isResolvingFaceBoundaries"
          data-testid="convert-entities-select-all-inner-loops-button"
          @click="handleSelectAllInnerLoops"
        >
          选择所有内环
        </n-button>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { PropertyManagerContext } from '@/core';

/**
 * Convert Entities 属性面板参数。
 */
interface SketchConvertEntitiesPropertySectionProps {
  /** PropertyManager 上下文。 */
  context: PropertyManagerContext;
}

const props = defineProps<SketchConvertEntitiesPropertySectionProps>();

/**
 * 当前 Convert Entities 会话状态。
 */
const convertEntitiesState = computed(() => {
  return props.context.convertEntitiesState ?? {
    active: false,
    targetSketchGroupId: null,
    selectedItems: [],
    asConstruction: false,
    selectChain: false,
    innerLoopsOneByOne: false,
    canSelectAllInnerLoops: false,
    isResolvingFaceBoundaries: false,
    canConfirm: false,
  };
});

/**
 * 当前已选实体列表。
 */
const selectedItems = computed(() => {
  return convertEntitiesState.value.selectedItems ?? [];
});

/**
 * 当前是否以构造几何方式创建目标实体。
 */
const constructionChecked = computed(() => {
  return convertEntitiesState.value.asConstruction === true;
});

/**
 * 当前是否启用草图链选择。
 */
const selectChainChecked = computed(() => {
  return convertEntitiesState.value.selectChain === true;
});

/**
 * 当前是否启用逐个 inner Wire 选择。
 */
const innerLoopsOneByOneChecked = computed(() => {
  return convertEntitiesState.value.innerLoopsOneByOne === true;
});

/**
 * 当前是否允许选择全部内环。
 */
const canSelectAllInnerLoops = computed(() => {
  return convertEntitiesState.value.canSelectAllInnerLoops === true;
});

/**
 * 当前是否正在解析 Face/Wire 边界。
 */
const isResolvingFaceBoundaries = computed(() => {
  return convertEntitiesState.value.isResolvingFaceBoundaries === true;
});

/**
 * 切换“作为构造线”创建选项。
 *
 * @param value 是否作为构造线。
 */
const handleConstructionToggle = (value: boolean): void => {
  props.context.setConvertEntitiesAsConstruction?.(value);
};

/**
 * 切换“选择链”选项。
 *
 * @param value 是否启用链选择。
 */
const handleSelectChainToggle = (value: boolean): void => {
  props.context.setConvertEntitiesSelectChain?.(value);
};

/**
 * 切换“逐个内环”选择模式。
 *
 * @param value 是否提升后续 inner Wire 成员点击。
 */
const handleInnerLoopsOneByOneToggle = (value: boolean): void => {
  props.context.setConvertEntitiesInnerLoopsOneByOne?.(value);
};

/**
 * 选择当前已选面的全部 inner Wires。
 */
const handleSelectAllInnerLoops = (): void => {
  void props.context.selectAllConvertEntitiesInnerLoops?.();
};

/**
 * 移除单个已选实体。
 *
 * @param selectionId 选择项稳定键。
 */
const handleRemoveSelection = (selectionId: string): void => {
  props.context.removeConvertEntitiesSelection?.(selectionId);
};
</script>

<style scoped>
.sketch-convert-entities-property-section {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.panel-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 0 14px;
  border-bottom: 1px solid var(--n-border-color, #e5e7eb);
}

.panel-section:last-child {
  padding-bottom: 0;
  border-bottom: 0;
}

.panel-block__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.panel-block__title {
  font-size: 13px;
  font-weight: 600;
  color: var(--n-text-color, #1f2937);
}

.panel-block__meta {
  font-size: 12px;
  color: var(--n-text-color-3, #9ca3af);
}

.selection-box {
  box-sizing: border-box;
  height: 92px;
  padding: 8px;
  border: 1px solid var(--n-border-color, #d9dee8);
  border-radius: 4px;
  background: var(--n-input-color, var(--n-color, #ffffff));
  overflow-y: auto;
}

.selection-box--empty {
  background: color-mix(in srgb, var(--n-primary-color, var(--puppy-primary)) 10%, var(--n-color, #ffffff));
  border-color: var(--n-primary-color, var(--puppy-primary));
}

.selection-box__empty {
  padding: 2px;
  font-size: 12px;
  line-height: 1.4;
  color: var(--n-text-color-3, #9ca3af);
}

.selection-box__list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0;
  margin: 0;
  list-style: none;
}

.selection-box__list-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 26px;
  padding: 4px 6px 4px 8px;
  border: 1px solid color-mix(in srgb, var(--n-primary-color, var(--puppy-primary)) 18%, transparent);
  border-radius: 3px;
  background: color-mix(in srgb, var(--n-primary-color, var(--puppy-primary)) 8%, transparent);
}

.selection-box__list-label {
  flex: 1;
  min-width: 0;
  color: var(--n-text-color, #1f2937);
  font-size: 12px;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.selection-box__remove-button {
  flex: none;
  border: 0;
  border-radius: 3px;
  padding: 3px 6px;
  background: transparent;
  color: var(--n-primary-color, var(--puppy-primary));
  font-size: 12px;
  line-height: 1.2;
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.selection-box__remove-button:hover {
  background: color-mix(in srgb, var(--n-error-color, #d03050) 10%, transparent);
  color: var(--n-error-color, #d03050);
}

.option-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  font-size: 12px;
  color: var(--n-text-color, #1f2937);
}
</style>
